import React, { useEffect } from "react";
import styled from "styled-components";
import { palette } from "@/styles/palette";
import { useNavigate, useLocation } from "react-router-dom";
import ReviewDetail from "@/components/review/ReviewDetail";
import { AromaCategory } from "@/types";
import { getImageUrl, ReviewInfo } from "@/api/soollogApi";

interface LocationState {
    reviewData: ReviewInfo;
}

const ReviewDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as LocationState;
    const reviewData = state?.reviewData;

    // 데이터가 없으면 홈으로 리다이렉트 처리
    useEffect(() => {
        if (!reviewData) {
            alert("리뷰 정보를 찾을 수 없습니다.");
            navigate("/home");
        }
    }, [reviewData, navigate]);

    // 데이터가 없으면 로딩 상태 또는 에러 메시지 표시
    if (!reviewData) {
        return (
            <ErrorContainer>
                <ErrorMessage>리뷰 정보를 찾을 수 없습니다.</ErrorMessage>
            </ErrorContainer>
        );
    }

    // 아로마 태그를 카테고리별로 그룹화
    const getAromaTags = (): AromaCategory[] => {
        // reviewAromaInfoList에서 카테고리별로 그룹화
        const categoryMap = new Map<string, Array<{ id: number; name: string; code: string }>>();

        reviewData.reviewAromaInfoList.forEach((aroma) => {
            if (!categoryMap.has(aroma.category)) {
                categoryMap.set(aroma.category, []);
            }
            categoryMap.get(aroma.category)?.push({
                id: aroma.id,
                name: aroma.name,
                code: aroma.code
            });
        });

        const aromaTags: AromaCategory[] = [];
        categoryMap.forEach((aromaList, category) => {
            aromaTags.push({
                category,
                aromaList
            });
        });

        return aromaTags;
    };

    // 맛 정보 매핑
    const getTasteValues = () => {
        return {
            sweetness: reviewData.sweetness,
            sourness: reviewData.sourness,
            freshness: reviewData.aromaStrength,
            thickness: reviewData.carbonation
        };
    };

    const handleBackButtonClick = () => {
        navigate(-1); // 이전 페이지로 이동
    };

    return (
        <Container>
            <Header>
                <BackButton onClick={handleBackButtonClick}>
                    <img src="/icon/chevron-left.png" alt="뒤로가기" />
                </BackButton>
                <Title>리뷰</Title>
            </Header>

            <ReviewDetail
                productInfo={{
                    name: reviewData.reviewProductInfo.productName,
                    thumbnailImageUrl:
                        reviewData.reviewImageInfoList.length > 0
                            ? getImageUrl(reviewData.reviewImageInfoList[0].imageUrl)
                            : "/images/default-alcohol.png",
                    type: reviewData.reviewProductInfo.type,
                    abv: reviewData.reviewProductInfo.abv || 0,
                    breweryName: "", // 제조사 정보가 없는 경우 빈 문자열 사용
                    productAltKey: reviewData.reviewProductInfo.productAltKey // 상품 상세 페이지로 이동하기 위한 키 추가
                }}
                rating={reviewData.rating}
                reviewDate={reviewData.reviewDate}
                content={reviewData.content}
                tasteValues={getTasteValues()}
                selectedTags={reviewData.reviewAromaInfoList.map((aroma) => aroma.id)}
                aromaTags={getAromaTags()}
                reviewImages={reviewData.reviewImageInfoList.map((img) => getImageUrl(img.imageUrl))}
                userNickname={""}
            />
        </Container>
    );
};

export default ReviewDetailPage;

const Container = styled.div`
    width: 100%;
    min-height: 100vh;
    background-color: ${palette.white};
    position: relative;
    overflow-x: hidden;
`;

const Header = styled.header`
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    padding: 0 16px;
    background-color: ${palette.white};
    z-index: 100;
`;

const BackButton = styled.button`
    position: absolute;
    left: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;

    img {
        width: 24px;
        height: 24px;
    }
`;

const Title = styled.h1`
    font-size: 18px;
    font-weight: 600;
    color: ${palette.black};
`;

const ErrorContainer = styled.div`
    width: 100%;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${palette.white};
`;

const ErrorMessage = styled.p`
    font-size: 18px;
    color: ${palette.red};
    text-align: center;
`;
